/**
 * Comprime imagem antes do upload pra ficar dentro do limite Meta (5MB pra image).
 *
 * Estratégia:
 *  1. Se já está pequena (≤ targetMaxBytes) E nas dimensões aceitáveis, retorna o
 *     blob original sem transcodar (preserva formato — PNG fica PNG, etc).
 *  2. Senão: redimensiona pra `maxDim` no maior lado (preserva aspect-ratio) e
 *     re-encoda como JPEG. Se ainda exceder, vai reduzindo a qualidade até caber.
 *
 * Tudo via Canvas API — zero dependências extras.
 */

export interface CompressOptions {
  /** Tamanho máximo aceito sem reencode. Default 5MB (limite Meta image). */
  targetMaxBytes?: number;
  /** Dimensão máxima do maior lado em px. Default 1920 (Full-HD). */
  maxDim?: number;
  /** Qualidade JPEG inicial (0..1). Default 0.85. */
  initialQuality?: number;
  /** Qualidade mínima antes de desistir. Default 0.5. */
  minQuality?: number;
  /** Força reencode pra JPEG mesmo quando o original já caberia. Útil pra
   *  bot que reusa a mídia em N envios — vale comprimir sempre pra JPEG. */
  forceJpeg?: boolean;
}

const DEFAULTS: Required<CompressOptions> = {
  targetMaxBytes: 5 * 1024 * 1024,
  maxDim: 1920,
  initialQuality: 0.85,
  minQuality: 0.5,
  forceJpeg: false,
};

/**
 * Resultado da compressão: blob (possivelmente re-encoded) + dimensões finais.
 * `width`/`height` é null pra non-image/GIF/SVG (não passa por canvas).
 */
export interface CompressResult {
  blob: Blob;
  width: number | null;
  height: number | null;
}

/**
 * Comprime uma imagem se necessário. Aceita File ou Blob.
 * Retorna `{ blob, width, height }`:
 *   - Se imagem original já cabe e é razoável → blob original com dims originais
 *   - Se redimensionou → blob JPEG com dims pós-resize
 *   - Non-image/GIF/SVG → blob original + dims null
 *
 * Dims são usadas pelo frontend pra reservar espaço da bolha (aspect-ratio)
 * antes do upload completar — bolha optimistic nasce dimensionada igual inbound.
 */
export async function compressImage(file: Blob, opts: CompressOptions = {}): Promise<CompressResult> {
  const { targetMaxBytes, maxDim, initialQuality, minQuality, forceJpeg } = { ...DEFAULTS, ...opts };

  // Não é imagem? devolve como veio (sem dims).
  if (!file.type.startsWith('image/')) return { blob: file, width: null, height: null };
  // GIF animado / SVG — não dá pra reencode em canvas sem perder; skip.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return { blob: file, width: null, height: null };
  }

  const img = await loadImage(file);
  const { naturalWidth: w0, naturalHeight: h0 } = img;
  const longestSide = Math.max(w0, h0);
  const fitsSize = file.size <= targetMaxBytes;
  const fitsDim = longestSide <= maxDim;

  // Sem forceJpeg: só re-encoda se necessário (preserva PNG quando cabe).
  // Com forceJpeg: sempre re-encoda pra JPEG q85 (PNG/JPG/WEBP/etc) — bot
  // reusa a mídia em N envios, vale comprimir bem 1x.
  if (fitsSize && fitsDim && !forceJpeg) {
    return { blob: file, width: w0, height: h0 };
  }

  let width = w0;
  let height = h0;
  if (longestSide > maxDim) {
    const scale = maxDim / longestSide;
    width = Math.round(w0 * scale);
    height = Math.round(h0 * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { blob: file, width: w0, height: h0 }; // sem fallback razoável
  // Pinta fundo branco antes do drawImage — JPEG não tem alpha, então PNG/WEBP
  // com transparência ficaria com fundo preto (default do canvas). Branco é
  // muito mais aceitável visualmente pra logos/screenshots.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  // Tenta JPEG com qualidade decrescente até caber em targetMaxBytes
  let q = initialQuality;
  let result: Blob = file;
  while (q >= minQuality) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', q);
    if (blob && blob.size <= targetMaxBytes) {
      result = blob;
      break;
    }
    if (blob) result = blob; // guarda a tentativa anterior caso desista
    q = Math.round((q - 0.1) * 100) / 100;
  }
  return { blob: result, width, height };
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), mime, quality);
  });
}
